// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';
import { dispatch } from '../index';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    checkout: {
        step: 0,
        products: [],
        subtotal: 0,
        total: 0,
        gst: 0,
        discount: 0,
        shipping: 0,
        billing: null,
        payment: {
            type: 'free',
            method: 'cod',
            card: ''
        }
    }
};

const calculateCartTotals = (checkout) => {
    let subtotal = 0;
    let gst = 0;
    if (Array.isArray(checkout.products)) {
        checkout.products.forEach((item) => {
            const price = Number(item.offerPrice || item.salePrice || 0);
            const qty = Number(item.quantity || 1);
            const itemTotal = price * qty;
            const rate = Number(item.gst || 0);
            subtotal += itemTotal;
            gst += (itemTotal * rate) / 100;
        });
    }
    const discount = Number(checkout.discount || 0);
    const shipping = Number(checkout.shipping || 0);
    const total = Math.max(0, subtotal - discount + shipping + gst);

    checkout.subtotal = Number(subtotal.toFixed(2));
    checkout.gst = Number(gst.toFixed(2));
    checkout.total = Number(total.toFixed(2));
};

const slice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
        },

        // ADD PRODUCT
        addProductSuccess(state, action) {
            state.checkout.products = action.payload.products;
            calculateCartTotals(state.checkout);
        },

        // REMOVE PRODUCT
        removeProductSuccess(state, action) {
            state.checkout.products = action.payload.products;
            calculateCartTotals(state.checkout);
        },

        // UPDATE PRODUCT
        updateProductSuccess(state, action) {
            state.checkout.products = action.payload.products;
            calculateCartTotals(state.checkout);
        },

        // SET STEP
        setStepSuccess(state, action) {
            state.checkout.step = action.payload;
        },

        // SET NEXT STEP
        setNextStepSuccess(state) {
            state.checkout.step += 1;
        },

        // SET BACK STEP
        setBackStepSuccess(state) {
            state.checkout.step -= 1;
        },

        // SET BILLING ADDRESS
        setBillingAddressSuccess(state, action) {
            state.checkout.billing = action.payload.billing;
        },

        // SET DISCOUNT
        setDiscountSuccess(state, action) {
            state.checkout.discount = Number(action.payload.amount || 0);
            calculateCartTotals(state.checkout);
        },

        // SET SHIPPING CHARGE
        setShippingChargeSuccess(state, action) {
            state.checkout.shipping = Number(action.payload.shipping || 0);
            state.checkout.payment = {
                ...state.checkout.payment,
                type: action.payload.type
            };
            calculateCartTotals(state.checkout);
        },

        // SET PAYMENT METHOD
        setPaymentMethodSuccess(state, action) {
            state.checkout.payment = {
                ...state.checkout.payment,
                method: action.payload.method
            };
        },

        // SET PAYMENT CARD
        setPaymentCardSuccess(state, action) {
            state.checkout.payment = {
                ...state.checkout.payment,
                card: action.payload.card
            };
        },

        // RESET CART
        resetCardSuccess(state) {
            state.checkout = initialState.checkout;
        },

        // SET CART TOTALS
        setCartTotalsSuccess(state, action) {
            if (action.payload.subtotal !== undefined) state.checkout.subtotal = Number(action.payload.subtotal);
            if (action.payload.gst !== undefined) state.checkout.gst = Number(action.payload.gst);
            if (action.payload.total !== undefined) state.checkout.total = Number(action.payload.total);
        },

        // SYNC PRODUCT GST
        syncProductGstSuccess(state, action) {
            const productGstMap = action.payload || {};
            if (Array.isArray(state.checkout.products)) {
                state.checkout.products = state.checkout.products.map((item) => {
                    const matchGst =
                        productGstMap[item.id] ??
                        productGstMap[item.ProductID] ??
                        productGstMap[item.productCode];
                    if (matchGst !== undefined && matchGst !== null) {
                        return { ...item, gst: Number(matchGst) };
                    }
                    return item;
                });
                calculateCartTotals(state.checkout);
            }
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

export function addProduct(product, products) {
    return async () => {
        try {
            const response = await axios.post('/api/cart/add', { product, products });
            console.log(response)
            dispatch(slice.actions.addProductSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function removeProduct(id, products) {
    return async () => {
        try {
            const response = await axios.post('/api/cart/remove', { id, products });
            dispatch(slice.actions.removeProductSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function updateProduct(id, quantity, products) {
    return async () => {
        try {
            const response = await axios.post('/api/cart/update', { id, quantity, products });
            dispatch(slice.actions.updateProductSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function setStep(step) {
    return () => {
        dispatch(slice.actions.setStepSuccess(step));
    };
}

export function setNextStep() {
    return () => {
        dispatch(slice.actions.setNextStepSuccess({}));
    };
}

export function setBackStep() {
    return () => {
        dispatch(slice.actions.setBackStepSuccess({}));
    };
}

export function setBillingAddress(address) {
    return async () => {
        try {
            const response = await axios.post('/api/cart/billing-address', { address });
            dispatch(slice.actions.setBillingAddressSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function setDiscount(code, total) {
    return async () => {
        try {
            const response = await axios.post('/api/cart/discount', { code, total });
            dispatch(slice.actions.setDiscountSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function setShippingCharge(charge, shipping) {
    return async () => {
        try {
            const response = await axios.post('/api/cart/shipping-charge', { charge, shipping });
            dispatch(slice.actions.setShippingChargeSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function setPaymentMethod(method) {
    return async () => {
        try {
            const response = await axios.post('/api/cart/payment-method', { method });
            dispatch(slice.actions.setPaymentMethodSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function setPaymentCard(card) {
    return async () => {
        try {
            const response = await axios.post('/api/cart/payment-card', { card });
            dispatch(slice.actions.setPaymentCardSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function resetCart() {
    return async () => {
        try {
            const response = await axios.post('/api/cart/reset');
            dispatch(slice.actions.resetCardSuccess(response.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function setCartTotals(totals) {
    return () => {
        dispatch(slice.actions.setCartTotalsSuccess(totals));
    };
}

export function syncProductGst(productGstMap) {
    return () => {
        dispatch(slice.actions.syncProductGstSuccess(productGstMap));
    };
}

